<script setup>
import {computed, onMounted, ref} from "vue";
import ShippingModal from "./ShippingModal.vue";
import useUser from "./UserComposition";
import useShipping from "./ShippingComposition";
import useThemeVoice from "./VoiceThemerComposition";
import AnswerModal from "./AnswerModal.vue";
import CompleteModal from "./CompleteModal.vue";

const siteUrl = document.querySelector('meta[name="site-url"]').content;

// 現行の URL から対象 ID を取得
const id = window.location.pathname.split('/').pop().pop();

// 各種情報
const shippingComposition = useShipping(siteUrl);
const userComposition = useUser();
const voiceThemeComposition = useThemeVoice(siteUrl, userComposition);

// エラー情報
const errors = ref({});

// 住所情報編集中かどうか
const isShippingEdit = ref(false);

// モーダルを開いているかどうか
const isShowAnswerModal = ref(false);

// question_type = 3 の場合のみ使用する、理由入力欄を表示するかどうか
const isShowReasonModal = ref(false);

// 連打防止用フラグ
const isSubmitting = ref(false);

// メインの登録フローにおいて、現在なんのモーダルを開いておくか
const currentModal = computed(() => {
  // 何も開かない場合は空文字を返す
  if ( ! isShowAnswerModal.value) {
    return '';
  }

  // 既に回答済の場合は回答済モーダルを返す
  if (voiceThemeComposition.isAnswered.value) {
    return 'answered';
  }

  // 住所情報が不足している場合は住所情報モーダルを返す
  if ( ! userComposition.isShipping.value && Number(voiceThemeComposition.voiceTheme.value.voice_gift.type) === 2) {
    return 'shipping';
  }

  // question_type が 1 の場合は自由入力モーダルを返す
  if (Number(voiceThemeComposition.voiceTheme.value.question_type) === 1) {
    return 'free';
  }

  // question_type が 2 の場合は選択肢モーダルを返す
  if (Number(voiceThemeComposition.voiceTheme.value.question_type) === 2) {
    return 'choose';
  }

  // 以下は question_type が 3 の場合だが、理由入力欄が表示されるかどうかで場合分け
  return isShowReasonModal.value ? 'reason' : 'select';
});

// 必要情報を同期する
const init = async () => {
  // お題の取得
  try {
    // サーバに問い合わせ
    const response = await fetch(document.querySelector('meta[name="site-url"]').content+'voice/get-information/'+id);

    // JSON 形式で返却されたデータを取得
    const data = await response.json();

    // 取得したデータを設定
    voiceThemeComposition.initVoiceTheme(data.voiceTheme);
    if (data.user !== null) {
      userComposition.initUser(data.user);
      shippingComposition.initUser(data.user);
    }
  }

    // おそらくセッション切れ
  catch (e) {
    alert('データの取得に失敗しました。画面をスーパーリロードして下さい。');
  }
};

// 住所情報を設定する
const setShipping = async (isValidate = false) => {
  // サーバに問い合わせ
  const result = await shippingComposition.setShipping(isValidate);

  // 成功かつ、バリデーションでなければユーザデータを更新
  if (result && ! isValidate) {
    userComposition.initUser(shippingComposition.userLocal);
  }

  // 成功かつバリデーションであれば確認画面へ
  if (result && isValidate) {
    isShippingEdit.value = true;
  }
};

// 回答を設定する
const setAnswer = async (payload) => {
  // 連打防止
  if (isSubmitting.value) {
    return;
  }

  // 連打防止フラグを立てる
  isSubmitting.value = true;

  // サーバに問い合わせ
  const result = await voiceThemeComposition.setAnswer(payload.nicknameId, payload.answer, payload.choiceIds);

  // 連打防止フラグを解除
  setTimeout(() => {
    isSubmitting.value = false;
  }, 3000);

  // 成功かつ、バリデーションでなければデータを更新
  if (result) {
    // データ更新
    init();

    // 素の HTML 部分を更新
    window.dispatchEvent(new CustomEvent('syncVoices'));
  }
};

// 初期化
onMounted(async () => {
  // 情報同期
  init();

  // モーダルを開くイベントを受け取る
  window.addEventListener('openVoiceModal', () => {
    isShowAnswerModal.value = true;
  });
});
</script>

<template>
  <div>
    <teleport to="body">
      <ShippingModal
        :is-show="currentModal === 'shipping'"
        :is-edit="isShippingEdit"
        :errors="shippingComposition.errors"
        :user="shippingComposition.userLocal"
        @request-close="isShowAnswerModal = false"
        @request-submit="setShipping(false)"
        @update-field="shippingComposition.updateUserField"
        @request-valid="setShipping(true)"
        @request-back="isShippingEdit = false"
      />
    </teleport>
    <teleport to="body">
      <AnswerModal
        :is-show="currentModal !== 'shipping' && currentModal !== '' && ! voiceThemeComposition.isAnswered.value"
        :current-modal="currentModal"
        :errors="voiceThemeComposition.errors"
        :user="userComposition.user"
        :voice-theme="voiceThemeComposition.voiceTheme"
        @request-close="isShowAnswerModal = false"
        @set-submit="setAnswer"
      />
    </teleport>
    <teleport to="body">
      <CompleteModal
        :is-show="isShowAnswerModal && voiceThemeComposition.isAnswered.value"
        :voice-theme="voiceThemeComposition.voiceTheme"
        @request-close="isShowAnswerModal = false"
      />
    </teleport>
  </div>
</template>
